import { useQuery } from '@tanstack/react-query';
import { HTTPError } from '@toss/ky';
import { useEffect } from 'react';

import { aiCoreApi } from '@/services/aiCoreClient';
import { eFitnessKeys } from '@/services/queryClient';

import { useUser } from './useUser';

const POLLING_INTERVAL = 15 * 60 * 1000; // Check every 15 minutes

/**
 * Keeps eFitness access token fresh by refreshing it when it's about to expire
 * using the refresh token.
 */
export function useEfitnessRefreshToken() {
  const { eFitnessUser, isEfitnessAuthenticated, logout, login, user } =
    useUser();

  const { data } = useQuery({
    queryKey: [eFitnessKeys.refreshToken()],
    queryFn: async () => {
      if (!eFitnessUser?.refreshToken) {
        return null;
      }

      // Skip refresh if clubId is not yet available
      if (!user?.clubId) {
        return null;
      }

      try {
        return await aiCoreApi.eFitness.auth.refresh(
          {
            clubId: user.clubId,
            refreshToken: eFitnessUser.refreshToken,
          },
          {
            retry: 0,
          },
        );
      } catch (error) {
        if (error instanceof HTTPError) {
          const status = error.response.status;
          // Only logout on actual auth failure
          if (status === 401) {
            logout();
          } else {
            try {
              const response = await error.response.json();
              console.error(response);
            } catch {}
          }
        }

        // Do not logout on non-HTTP or validation errors
        return null;
      }
    },
    enabled: isEfitnessAuthenticated() && !!user?.clubId,
    refetchInterval: POLLING_INTERVAL,
    retry: false,
  });

  // Update user login with new tokens and data
  useEffect(() => {
    if (!data) {
      return;
    }

    login(data.user, data.token, data.eFitness);
  }, [data, login]);
}
